import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch } from '../../../redux/hooks';
import { fetchSettings } from '../../../redux/slices/AccountLevelSlice';
import MyButton from '../../../shared-components/button/Button';
import InputWithDropDown from '../../../shared-components/input-with-drop-down/InputWithDropDown';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import { permissionCheck } from '../../../shared-functions/JWTDocodeFunction';
import feeConfigurationApi from '../../../utils/api/fee-configuration';

interface PlatformLevelInterface {
  _id: string;
  platformFee: string;
  platformFeeType: 'amount' | 'percentage';
  sessionFee: string;
  sessionFeeType: 'amount' | 'percentage';
}

/**
 * Platform level component
 */
function PlatformLevel() {
  const { t, i18n } = useTranslation('sharedComponent');
  const [showSuccess, setShowSuccess] = useState(false);
  const [data, setData] = useState<PlatformLevelInterface | null>(null);
  const dispatch = useAppDispatch();

  /** Fetch configuration */
  useEffect(() => {
    /**
     * Function for fetching fee configuration data
     */
    const fetchFeeConfiguration = async () => {
      try {
        const response = await feeConfigurationApi.fetchPlatformLevel();
        setData(response.data);
      } catch (error) {
        console.error('Error fetching fee configuration:', error);
      }
    };
    fetchFeeConfiguration();
  }, []);

  /** Generic updater */
  const updateField = useCallback(
    <K extends keyof PlatformLevelInterface>(field: K, value: PlatformLevelInterface[K]) => {
      setData((previous) => (previous ? { ...previous, [field]: value } : previous));
    },
    [],
  );

  /**
   * Handle fee change with 2 decimal logic
   * @param {'platformFee' | 'sessionFee'} field - The field to update
   * @param {string} value - The fee value
   */
  const handleFeeChange = (field: 'platformFee' | 'sessionFee', value: string) => {
    let inputValue = value.replace(/[^0-9.]/g, '');
    const parts = inputValue.split('.');
    if (parts.length > 2) {
      inputValue = `${parts[0]}.${parts[1]}`;
    }
    if (parts[1] && parts[1].length > 2) {
      inputValue = `${parts[0]}.${parts[1].slice(0, 2)}`;
    }
    updateField(field, inputValue);
  };

  /**
   * Save changes
   */
  const handleSave = async () => {
    if (!data) return;
    try {
      await feeConfigurationApi.updatePlatformLevel({
        platformFee: data.platformFee,
        platformFeeType: data.platformFeeType,
        sessionFee: data.sessionFee,
        sessionFeeType: data.sessionFeeType,
      });
      dispatch(fetchSettings());
      setShowSuccess(true);
    } catch (error) {
      console.error('Error saving fee configuration:', error);
    }
  };

  return (
    <div className="platform-level-wrap" dir={i18n.dir()}>
      <InputWithDropDown
        label={t('platformFee')}
        value={data?.platformFee}
        mode={data?.platformFeeType ?? 'amount'}
        onChangeValue={(value) => handleFeeChange('platformFee', value)}
        onChangeMode={(mode) => updateField('platformFeeType', mode)}
      />

      <InputWithDropDown
        label={t('sessionFee')}
        value={data?.sessionFee}
        mode={data?.sessionFeeType ?? 'amount'}
        onChangeValue={(value) => handleFeeChange('sessionFee', value)}
        onChangeMode={(mode) => updateField('sessionFeeType', mode)}
      />

      <MyButton
        label={t('save', 'Save')}
        buttonType="submit"
        onClickFunc={handleSave}
        disableButton={!permissionCheck('mobilityPlatformLevelUpdate')}
      />
      <ModalBox
        open={showSuccess}
        title=""
        content={
          <SuccessNotification
            title={t('success', 'Success')}
            description={t('successDescription', 'Fee Configuration updated Successfully')}
            notificationCloseTimer={3000}
            onNotificationClose={() => setShowSuccess(false)}
          />
        }
        closeModal={() => setShowSuccess(false)}
        header={false}
        rightCorner=""
        notification
      />
    </div>
  );
}

export default PlatformLevel;
